/**
 * The critter half of the wield test: which art base a critter pid uses, and
 * which critter `.frm` files this install actually ships.
 *
 * Two lists and one probe:
 *
 *   - `proto/critters/critters.lst` — 1-based and positional, exactly like the
 *     item list (`_proto_list_str`, engine/src/proto.cc:201). Line N names the
 *     `.pro` for critter proto number N, and a critter pid is
 *     `OBJ_TYPE_CRITTER << 24 | N`.
 *   - `proto/critters/<file>.pro` — only the third int32 is wanted. `pid`,
 *     `messageId` and `fid` lead *every* proto regardless of object type
 *     (`protoRead`, engine/src/proto.cc:1675), so the critter's art id is a
 *     big-endian read at offset 8. Nothing else in a critter proto is parsed;
 *     the editor has no business with the rest of it.
 *   - `art/critters/critters.lst` — `artReadList` (engine/src/art.cc:721)
 *     truncates each line at the first of `" ,;\r\t\n"`, so `hmjmps,11,1`
 *     becomes `hmjmps`. Index it with `fid & 0xFFF`; a `fid` past the end is
 *     what makes `artBuildFilePath` return null.
 *
 * ## Existence, and where this is honest about its limits
 *
 * `artExists` (engine/src/art.cc:927) opens the path through the whole xbase
 * chain. `GameFiles.list` enumerates the archives only — the engine does not
 * enumerate the loose `data/` directory either, and neither can we. So a
 * folder install, or a mod that drops loose critter art, is invisible here.
 *
 * That failure is one-directional: a loose file can only *add* art, so the
 * worst case is calling something unwieldable that the install would allow.
 * Rather than ship a badge that is quietly wrong on a whole class of install,
 * `ok` is false when the archives yield no critter art at all, and the caller
 * hides the feature. A partial answer about what the engine will refuse is
 * worse than no answer, because the player cannot tell which one they got.
 *
 * ## Cost
 *
 * The proto reads are lazy and memoised per pid. Resolving every one of the
 * ~480 critter protos up front would be ~480 inflates out of master.dat for a
 * question that is only ever asked about the two to five critters in the
 * player's party.
 */

import { parseLst, lookupProtoFile, type LstFile } from '../mods/lst-codec';
import type { GameFiles } from './files';

/** `artReadList`, engine/src/art.cc:721 — the line up to the first separator. */
const listName = (line: string): string => line.split(/[ ,;\r\t\n]/)[0]!.trim().toLowerCase();

export interface CritterArt {
  /**
   * True when this install's archives supplied both critter lists and some
   * critter art. False means "do not ask" — see the note above.
   */
  ok: boolean;
  /** Whether `art/critters/<name>.frm` is in the archives. Path is lowercased. */
  hasArt(path: string): boolean;
  /**
   * The `art/critters/critters.lst` base name for a critter pid, resolved and
   * memoised. Null when this install has no such proto, or its `fid` points
   * past the art list.
   */
  artBase(pid: number): Promise<string | null>;
  /**
   * The already-resolved answer, for a synchronous render. `undefined` means
   * "not asked yet" and is the caller's cue to call `artBase` and re-render.
   */
  resolvedArtBase(pid: number): string | null | undefined;
}

const NONE: CritterArt = {
  ok: false,
  hasArt: () => false,
  artBase: async () => null,
  resolvedArtBase: () => null,
};

/** `OBJ_TYPE_CRITTER << 24 | protoNumber`, per engine/src/proto_types.h. */
const OBJ_TYPE_CRITTER = 1;

export async function loadCritterArt(files: GameFiles): Promise<CritterArt> {
  const artLstBytes = await files.read('art/critters/critters.lst');
  const protoLstBytes = await files.read('proto/critters/critters.lst');
  if (!artLstBytes || !protoLstBytes) return NONE;

  let artNames: string[];
  let protoLst: LstFile;
  try {
    artNames = parseLst(artLstBytes).lines.map(listName);
    protoLst = parseLst(protoLstBytes);
  } catch {
    return NONE;
  }

  const art = new Set(
    files.list('art/critters/').filter((path) => path.toLowerCase().endsWith('.frm')).map((path) => path.toLowerCase())
  );
  if (art.size === 0 || artNames.length === 0) return NONE;

  const resolved = new Map<number, string | null>();
  const pending = new Map<number, Promise<string | null>>();

  const resolve = async (pid: number): Promise<string | null> => {
    if ((pid >>> 24) !== OBJ_TYPE_CRITTER) return null;
    const fileName = lookupProtoFile(protoLst, pid & 0xffffff);
    if (!fileName) return null;
    const bytes = await files.read(`proto/critters/${fileName}`);
    if (!bytes || bytes.length < 12) return null;
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    // The proto's own pid is the authority, same rule the item catalog uses: a
    // proto renumbered by hand is not one we can speak for.
    if (view.getInt32(0, false) !== pid) return null;
    return artNames[view.getInt32(8, false) & 0xfff] ?? null;
  };

  return {
    ok: true,
    hasArt: (path) => art.has(path),
    resolvedArtBase: (pid) => resolved.get(pid),
    artBase(pid) {
      if (resolved.has(pid)) return Promise.resolve(resolved.get(pid)!);
      let inFlight = pending.get(pid);
      if (!inFlight) {
        inFlight = resolve(pid)
          .catch(() => null)
          .then((base) => {
            resolved.set(pid, base);
            pending.delete(pid);
            return base;
          });
        pending.set(pid, inFlight);
      }
      return inFlight;
    },
  };
}
